/* ==========================================================================
   HP V2 — shared behaviour for the hp-v2-* sections.
   Guarded so it stays a no-op if the file is included more than once
   (each section drops its own script tag).
   ========================================================================== */
(function () {
  if (window.HPV2) return;

  var HPV2 = {};
  window.HPV2 = HPV2;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Only opt into the reveal animation once this script is running, so a
     failed or blocked load can never leave a section stuck at opacity 0. */
  document.documentElement.classList.add('hpv2-js');

  /* ---- Viewport width without the scrollbar ------------------------------ */
  /* Edge-to-edge rails use this instead of 100vw, which includes the
     scrollbar on desktop and would misalign the first card. */
  function setViewportWidth() {
    document.documentElement.style.setProperty('--hpv2-vw', document.documentElement.clientWidth + 'px');
  }
  setViewportWidth();
  window.addEventListener('resize', setViewportWidth);

  /* ---- Reveal on scroll -------------------------------------------------- */
  /* The stagger is worked out per batch of elements that enter the viewport
     together, and capped, so a card never waits on the index it has in the
     whole document (that is what made later rails take seconds to show). */
  var MAX_STAGGER = 0.3;
  var revealObserver = null;

  function show(node, delay) {
    if (delay) node.style.setProperty('--hpv2-delay', delay + 's');
    node.classList.add('hpv2-in');
  }

  if ('IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(function (entries) {
      var batch = 0;
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var node = entry.target;
        var step = parseFloat(node.getAttribute('data-hpv2-reveal')) || 0;
        show(node, Math.min(step * batch, MAX_STAGGER));
        if (step) batch++;
        revealObserver.unobserve(node);
      });
    // Trigger a little after the top edge enters, so the motion is seen
    // rather than finishing below the fold.
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
  }

  HPV2.reveal = function (root) {
    var nodes = (root || document).querySelectorAll('[data-hpv2-reveal]:not(.hpv2-in)');

    Array.prototype.forEach.call(nodes, function (node) {
      // Anything already on screen shows immediately - no stagger, no wait.
      if (!revealObserver || node.getBoundingClientRect().top < window.innerHeight) {
        show(node, 0);
        return;
      }
      revealObserver.observe(node);
    });

    // Safety net: if the observer never fires (background tab, odd viewport,
    // prerender), nothing may stay invisible.
    window.setTimeout(function () {
      var stuck = (root || document).querySelectorAll('[data-hpv2-reveal]:not(.hpv2-in)');
      Array.prototype.forEach.call(stuck, function (node) {
        if (node.getBoundingClientRect().top < window.innerHeight * 1.5) show(node, 0);
      });
    }, 2000);
  };

  /* ---- Rails ------------------------------------------------------------- */
  function railState(rail, nav, prev, next) {
    var scrollable = rail.scrollWidth - rail.clientWidth;

    if (nav) nav.classList.toggle('is-active', scrollable > 4);
    if (!prev || !next) return;

    prev.disabled = rail.scrollLeft <= 4;
    next.disabled = rail.scrollLeft >= scrollable - 4;
  }

  function railStep(rail, direction) {
    var first = rail.firstElementChild;
    var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    var amount = first ? first.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
    rail.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  /* Autoplay: advance one card every N seconds, wrap to the start at the
     end, and hold still while the shopper is hovering, touching, focused
     inside it, or it is off screen. */
  function railAutoplay(rail, seconds) {
    if (reduceMotion || !seconds) return;

    var held = false;
    var visible = true;

    function hold() { held = true; }
    function release() { held = false; }

    rail.addEventListener('mouseenter', hold);
    rail.addEventListener('mouseleave', release);
    rail.addEventListener('focusin', hold);
    rail.addEventListener('focusout', release);
    rail.addEventListener('touchstart', hold, { passive: true });
    rail.addEventListener('touchend', function () {
      // Give the swipe time to settle before resuming.
      window.setTimeout(release, seconds * 1000);
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }).observe(rail);
    }

    window.setInterval(function () {
      if (held || !visible || document.hidden) return;
      var scrollable = rail.scrollWidth - rail.clientWidth;
      if (scrollable <= 4) return;
      if (rail.scrollLeft >= scrollable - 4) {
        rail.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        railStep(rail, 1);
      }
    }, seconds * 1000);
  }

  HPV2.rails = function (root) {
    var rails = (root || document).querySelectorAll('[data-hpv2-rail]:not([data-hpv2-rail-ready])');

    Array.prototype.forEach.call(rails, function (rail) {
      rail.setAttribute('data-hpv2-rail-ready', 'true');

      // The arrows may live in the section header rather than beside the rail,
      // so look them up across the whole section.
      var scope = rail.closest('[data-section-id]') ||
                  rail.closest('.hpv2-rail-wrap') ||
                  rail.parentNode;
      var nav = scope.querySelector('.hpv2-rail-nav');
      var prev = scope.querySelector('[data-hpv2-rail-prev]');
      var next = scope.querySelector('[data-hpv2-rail-next]');

      function update() { railState(rail, nav, prev, next); }

      if (prev) prev.addEventListener('click', function () { railStep(rail, -1); });
      if (next) next.addEventListener('click', function () { railStep(rail, 1); });

      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);

      update();
      // Card images settle after load and can change the scroll width.
      window.addEventListener('load', update);

      railAutoplay(rail, parseFloat(rail.getAttribute('data-hpv2-autoplay')) || 0);
    });
  };

  /* ---- Slider (hero) ----------------------------------------------------- */
  HPV2.sliders = function (root) {
    var sliders = (root || document).querySelectorAll('[data-hpv2-slider]:not([data-hpv2-slider-ready])');

    Array.prototype.forEach.call(sliders, function (slider) {
      slider.setAttribute('data-hpv2-slider-ready', 'true');

      var slides = Array.prototype.slice.call(slider.querySelectorAll('[data-hpv2-slide]'));
      if (slides.length < 2) return;

      var dots = Array.prototype.slice.call(slider.querySelectorAll('[data-hpv2-slider-dot]'));
      var prev = slider.querySelector('[data-hpv2-slider-prev]');
      var next = slider.querySelector('[data-hpv2-slider-next]');
      var isSlide = slider.getAttribute('data-transition') === 'slide';
      var seconds = parseFloat(slider.getAttribute('data-autoplay')) || 0;
      var pauseOnHover = slider.hasAttribute('data-pause-on-hover');
      var current = 0;
      var timer = null;
      var paused = false;
      var visible = true;

      function goTo(index, direction) {
        index = (index + slides.length) % slides.length;
        if (index === current) return;

        var from = slides[current];
        var to = slides[index];
        if (direction === undefined) direction = index > current ? 1 : -1;

        if (isSlide && !reduceMotion) {
          // Park the incoming slide off to one side, then let both glide.
          to.style.transition = 'none';
          to.style.transform = 'translateX(' + (direction * 100) + '%)';
          void to.offsetWidth;
          to.style.transition = '';
          to.style.transform = '';
          from.style.transform = 'translateX(' + (direction * -100) + '%)';
          from.classList.add('is-leaving');
          window.setTimeout(function () {
            from.classList.remove('is-leaving');
            from.style.transform = '';
          }, 900);
        }

        from.classList.remove('is-active');
        from.setAttribute('aria-hidden', 'true');
        to.classList.add('is-active');
        to.removeAttribute('aria-hidden');

        dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === index);
          if (i === index) {
            dot.setAttribute('aria-current', 'true');
          } else {
            dot.removeAttribute('aria-current');
          }
        });

        current = index;
        restart();
      }

      function restart() {
        if (!seconds || reduceMotion) return;
        // Moving .is-active to the next indicator restarts its progress bar.
        schedule(seconds * 1000);
      }

      var remaining = 0;
      var startedAt = 0;

      function schedule(ms) {
        window.clearTimeout(timer);
        remaining = ms;
        startedAt = Date.now();
        if (paused || !visible) return;
        timer = window.setTimeout(function () { goTo(current + 1, 1); }, ms);
      }

      function pause() {
        if (paused) return;
        paused = true;
        slider.classList.add('is-paused');
        window.clearTimeout(timer);
        remaining = Math.max(0, remaining - (Date.now() - startedAt));
      }

      function resume() {
        if (!paused) return;
        paused = false;
        slider.classList.remove('is-paused');
        if (seconds && visible) schedule(remaining || seconds * 1000);
      }

      if (prev) prev.addEventListener('click', function () { goTo(current - 1, -1); });
      if (next) next.addEventListener('click', function () { goTo(current + 1, 1); });
      dots.forEach(function (dot) {
        dot.addEventListener('click', function () {
          goTo(parseInt(dot.getAttribute('data-hpv2-slider-dot'), 10));
        });
      });

      slider.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') goTo(current - 1, -1);
        if (event.key === 'ArrowRight') goTo(current + 1, 1);
      });

      // Swipe on touch screens.
      var touchX = null;
      var touchY = null;
      slider.addEventListener('touchstart', function (event) {
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
      }, { passive: true });
      slider.addEventListener('touchend', function (event) {
        if (touchX === null) return;
        var dx = event.changedTouches[0].clientX - touchX;
        var dy = event.changedTouches[0].clientY - touchY;
        touchX = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          goTo(current + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
        }
      }, { passive: true });

      if (seconds && !reduceMotion) {
        if (pauseOnHover) {
          slider.addEventListener('mouseenter', pause);
          slider.addEventListener('mouseleave', resume);
        }
        slider.addEventListener('focusin', pause);
        slider.addEventListener('focusout', resume);

        document.addEventListener('visibilitychange', function () {
          if (document.hidden) { pause(); } else { resume(); }
        });

        if ('IntersectionObserver' in window) {
          new IntersectionObserver(function (entries) {
            var wasVisible = visible;
            visible = entries[0].isIntersecting;
            if (visible === wasVisible) return;
            if (!visible) {
              // Off screen: freeze the timer and the progress bar together.
              window.clearTimeout(timer);
              remaining = Math.max(0, remaining - (Date.now() - startedAt));
              slider.classList.add('is-paused');
            } else if (!paused) {
              slider.classList.remove('is-paused');
              schedule(remaining || seconds * 1000);
            }
          }).observe(slider);
        }

        schedule(seconds * 1000);
      } else {
        slider.removeAttribute('data-autoplay');
      }

      // Theme editor: jump to the slide being edited.
      slider.addEventListener('shopify:block:select', function (event) {
        var i = slides.indexOf(event.target.closest('[data-hpv2-slide]'));
        if (i > -1) goTo(i);
        pause();
      });
      slider.addEventListener('shopify:block:deselect', resume);
    });
  };

  /* ---- Inline videos (creator wall) ------------------------------------- */
  /* Sources carry data-src so nothing downloads until the wall is close to
     the screen. The videos of one section play together while any part of
     that section is visible - on phones the wall is a swipe rail, so going by
     each card's own visibility left every card but the first paused - and
     pause together when it scrolls away. With reduced motion they never
     autoplay; the sound button starts one instead. */
  function loadVideo(video) {
    if (video.getAttribute('data-hpv2-loaded')) return;
    video.setAttribute('data-hpv2-loaded', 'true');
    Array.prototype.forEach.call(video.querySelectorAll('source[data-src]'), function (source) {
      source.src = source.getAttribute('data-src');
    });
    video.load();
  }

  function playVideo(video) {
    var attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(function () {});
  }

  HPV2.videos = function (root) {
    var videos = (root || document).querySelectorAll('[data-hpv2-video]:not([data-hpv2-video-ready])');
    var groups = [];

    Array.prototype.forEach.call(videos, function (video) {
      video.setAttribute('data-hpv2-video-ready', 'true');

      var group = video.closest('[data-section-id]') || video.parentNode;
      if (groups.indexOf(group) === -1) {
        groups.push(group);
        group.hpv2Videos = [];
      }
      group.hpv2Videos.push(video);

      // Should be playing right now (section on screen, autoplay allowed).
      video.hpv2Wanted = false;

      // A play() made before the clip had data, or one the browser dropped
      // (slow network, power saving), is retried as soon as it can play.
      ['canplay', 'loadeddata'].forEach(function (type) {
        video.addEventListener(type, function () {
          if (video.hpv2Wanted && video.paused) playVideo(video);
        });
      });

      var button = video.parentNode.querySelector('[data-hpv2-video-sound]');
      if (button) {
        button.addEventListener('click', function () {
          loadVideo(video);
          var on = video.muted;
          video.muted = !on;
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
          button.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
          video.hpv2Wanted = true;
          if (video.paused) playVideo(video);
        });
      }
    });

    groups.forEach(function (group) {
      var list = group.hpv2Videos;

      function setPlaying(on) {
        list.forEach(function (video) {
          if (on) {
            loadVideo(video);
            if (reduceMotion && video.muted) return;
            video.hpv2Wanted = true;
            if (video.paused) playVideo(video);
          } else {
            video.hpv2Wanted = false;
            video.pause();
          }
        });
      }

      if (!('IntersectionObserver' in window)) {
        setPlaying(true);
        return;
      }

      new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) list.forEach(loadVideo);
      }, { rootMargin: '900px 0px' }).observe(group);

      new IntersectionObserver(function (entries) {
        setPlaying(entries[0].isIntersecting);
      }, { threshold: 0.15 }).observe(group);
    });
  };

  /* ---- Marquee ----------------------------------------------------------- */
  /* Duplicates the track content until it comfortably overflows, so the
     CSS animation can loop without a visible seam. */
  HPV2.marquees = function (root) {
    var marquees = (root || document).querySelectorAll('[data-hpv2-marquee]:not([data-hpv2-marquee-ready])');

    Array.prototype.forEach.call(marquees, function (track) {
      track.setAttribute('data-hpv2-marquee-ready', 'true');

      var original = track.innerHTML;
      var guard = 0;

      while (track.scrollWidth < track.parentNode.clientWidth * 2 && guard < 8) {
        track.innerHTML += original;
        guard++;
      }
    });
  };

  /* ---- Parallax ---------------------------------------------------------- */
  /* Elements with data-hpv2-parallax="<factor>" drift against the scroll by
     factor x their distance from the middle of the screen. Desktop only, off
     with reduced motion, and capped so cards never wander far. */
  var parallaxNodes = [];
  var parallaxQueued = false;
  var parallaxQuery = window.matchMedia ? window.matchMedia('(min-width: 750px)') : null;
  var PARALLAX_MAX = 48;

  function parallaxFrame() {
    parallaxQueued = false;
    var active = !parallaxQuery || parallaxQuery.matches;
    var middle = window.innerHeight / 2;

    parallaxNodes.forEach(function (node) {
      if (!active) {
        node.style.translate = '';
        return;
      }
      var factor = parseFloat(node.getAttribute('data-hpv2-parallax')) || 0;
      // Measure without the current offset so the motion does not feed back.
      var rect = node.getBoundingClientRect();
      var current = parseFloat(node.getAttribute('data-hpv2-parallax-y')) || 0;
      var distance = rect.top - current + rect.height / 2 - middle;
      if (Math.abs(distance) > window.innerHeight * 1.5) return;
      var y = Math.max(-PARALLAX_MAX, Math.min(PARALLAX_MAX, -distance * factor));
      node.setAttribute('data-hpv2-parallax-y', y.toFixed(1));
      node.style.translate = '0 ' + y.toFixed(1) + 'px';
    });
  }

  function queueParallax() {
    if (parallaxQueued) return;
    parallaxQueued = true;
    window.requestAnimationFrame(parallaxFrame);
  }

  HPV2.parallax = function (root) {
    if (reduceMotion) return;
    var nodes = (root || document).querySelectorAll('[data-hpv2-parallax]:not([data-hpv2-parallax-ready])');
    if (!nodes.length) return;

    Array.prototype.forEach.call(nodes, function (node) {
      node.setAttribute('data-hpv2-parallax-ready', 'true');
      parallaxNodes.push(node);
    });
    // Drop nodes the theme editor has replaced.
    parallaxNodes = parallaxNodes.filter(function (node) { return document.documentElement.contains(node); });

    if (!HPV2._parallaxBound) {
      HPV2._parallaxBound = true;
      window.addEventListener('scroll', queueParallax, { passive: true });
      window.addEventListener('resize', queueParallax);
    }
    queueParallax();
  };

  /* ---- Boot -------------------------------------------------------------- */
  HPV2.init = function (root) {
    HPV2.reveal(root);
    HPV2.rails(root);
    HPV2.sliders(root);
    HPV2.videos(root);
    HPV2.marquees(root);
    HPV2.parallax(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { HPV2.init(); });
  } else {
    HPV2.init();
  }

  // Theme editor: re-run for the section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    HPV2.init(event.target);
  });
})();
